import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { SelectField } from '@brighte/form-components';
import {
  ApplicantType,
  BusinessDetailsType,
  EnterpriseStructure,
  FINANCIAL_PRODUCT,
  businessDetailsFormSchema,
  isExtendedStructure,
} from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { useApplicationData } from '@provider';
import { Text } from '@spark-web/text';
import { APPLICATION_STEPS, BUSINESS_DETAILS, LABELS } from '@utils';
import ApplicantDetailsFields from './ApplicantDetailsFields';
import BusinessDetailsFields from './BusinessDetailsFields';
import ExtendedBusinessDetailsFields from './ExtendedBusinessDetailsFields';
import { generateStructureOptions, useBeforeNextStep, useBusinessDetailsSummary } from './hooks';

// For extended structures the applicant IS the primary contact (Trustee #1 / Partner #1).
// Rather than showing a redundant "Tell us about yourself" section, we silently mirror
// the primary contact name into the applicant fields that the rest of the form relies on.
function ApplicantSyncFromPrimaryContact({
  watch,
  setValue,
}: Pick<UseFormReturn<BusinessDetailsType>, 'watch' | 'setValue'>) {
  const w = watch as (path: string) => string | undefined;
  const firstName = w('enterprise.primaryContact.firstName');
  const middleName = w('enterprise.primaryContact.middleName');
  const lastName = w('enterprise.primaryContact.lastName');

  useEffect(() => {
    const set = setValue as (name: string, value: string) => void;
    set('applicant.firstName', firstName ?? '');
    set('applicant.middleName', middleName ?? '');
    set('applicant.lastName', lastName ?? '');
  }, [firstName, middleName, lastName, setValue]);

  return null;
}

function BusinessDetails() {
  const { BusinessDetails: businessDetails, quote } = useApplicationData(['BusinessDetails', 'quote']);
  const { handleBeforeNextStep } = useBeforeNextStep();
  const { TITLE, DESCRIPTION } = BUSINESS_DETAILS;
  const { ENTITY_STRUCTURE } = LABELS;

  const financialProductId = quote?.financialProduct?.id;
  const structureOptions = generateStructureOptions(financialProductId);

  const enterprise = businessDetails?.enterprise;
  const quoteEnterprise = quote?.application?.enterprise;
  const defaultValues = {
    ...businessDetails,
    ...(financialProductId === FINANCIAL_PRODUCT.ACT_COMMUNITY && enterprise
      ? {
          enterprise: {
            ...enterprise,
            legalEntityName:
              ('legalEntityName' in enterprise ? enterprise.legalEntityName : undefined) ??
              quoteEnterprise?.legalEntityName ??
              undefined,
            tradingName:
              ('tradingName' in enterprise ? enterprise.tradingName : undefined) ??
              quoteEnterprise?.tradingName ??
              undefined,
            abn: ('abn' in enterprise ? enterprise.abn : undefined) ?? quote?.abn ?? undefined,
          },
        }
      : financialProductId === FINANCIAL_PRODUCT.ACT_COMMUNITY
        ? {
            enterprise: {
              legalEntityName: quoteEnterprise?.legalEntityName ?? undefined,
              tradingName: quoteEnterprise?.tradingName ?? undefined,
              abn: quote?.abn ?? undefined,
            },
          }
        : {}),
  };

  return (
    <StepNavigator<BusinessDetailsType>
      schema={businessDetailsFormSchema}
      name={APPLICATION_STEPS.BUSINESS_DETAILS}
      title={TITLE}
      defaultValues={defaultValues}
      isLoading={false}
      completedContent={useBusinessDetailsSummary()}
      onBeforeNextStep={handleBeforeNextStep}
    >
      {formContext => {
        const { control, watch, register, setValue, formState } = formContext;
        const structure = watch('enterprise.structure');
        const hasExtendedFields = isExtendedStructure(structure);
        const isCompanyOrAssociation =
          structure === EnterpriseStructure.Company ||
          structure === EnterpriseStructure.IncorporatedAssociation ||
          structure === EnterpriseStructure.UnincorporatedAssociation;

        const enterpriseError = formState.errors.enterprise as { message?: string } | undefined;

        return (
          <>
            <Text>{DESCRIPTION}</Text>
            <SelectField
              name="enterprise.structure"
              control={control}
              label={ENTITY_STRUCTURE}
              options={structureOptions}
              placeholder="Select"
              fieldProps={{
                tone: enterpriseError?.message ? 'critical' : undefined,
                message: enterpriseError?.message,
              }}
            />
            {hasExtendedFields && <ExtendedBusinessDetailsFields formContext={formContext} />}
            {isCompanyOrAssociation && <BusinessDetailsFields structure={structure} formContext={formContext} />}
            {hasExtendedFields ? (
              <ApplicantSyncFromPrimaryContact watch={watch} setValue={setValue} />
            ) : (
              <ApplicantDetailsFields control={control} setValue={setValue} />
            )}
            <input type="hidden" {...register('applicant.type')} value={ApplicantType.Primary} />
          </>
        );
      }}
    </StepNavigator>
  );
}

export default BusinessDetails;
